import React, { useState, useEffect } from 'react';
import { Bell, Send, Gift, Megaphone, Clock, CheckCircle, XCircle, Users, ChevronDown, ChevronUp, Trash2, RefreshCw } from 'lucide-react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';

const AdminNotifications = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [campaignRecipients, setCampaignRecipients] = useState({});
  const [customerCount, setCustomerCount] = useState(0);
  const [birthdayCount, setBirthdayCount] = useState(0);
  
  // Compose form
  const [composeData, setComposeData] = useState({
    title: '',
    body: '',
    type: 'promo' // 'promo' or 'announcement'
  });
  const [activeEmojiField, setActiveEmojiField] = useState(null); // 'title' or 'body'

  const emojiCategories = [
    { label: 'Promos & Deals', emojis: ['🔥', '⚡', '💥', '🎉', '🎊', '✨', '💫', '🌟', '⭐', '🏷️', '💰', '💸', '🤑', '💵', '🎁', '🎀', '🏆', '👑'] },
    { label: 'Shopping & Food', emojis: ['🛒', '🛍️', '🧺', '📦', '🥬', '🥩', '🍞', '🥛', '🧀', '🍎', '🍌', '🥚', '🍗', '🥤', '☕', '🍰', '🍫', '🍪'] },
    { label: 'Time & Urgency', emojis: ['⏰', '⏳', '🚨', '📢', '📣', '🔔', '❗', '‼️', '⚠️', '🆕', '🆓', '🔜', '📅', '🗓️'] },
    { label: 'Celebration', emojis: ['🎂', '🥳', '🎈', '🎉', '🎊', '🎶', '❤️', '💚', '💛', '🧡', '😊', '🙏', '👋', '👍', '💪', '🙌'] },
    { label: 'Delivery & Store', emojis: ['🚚', '📍', '🏪', '🏠', '✅', '☑️', '📝', '📋', '💬', '📱', '🔑', '🌍', '🇿🇦'] }
  ];

  const insertEmoji = (emoji) => {
    if (!activeEmojiField) return;
    setComposeData(prev => ({
      ...prev,
      [activeEmojiField]: prev[activeEmojiField] + emoji
    }));
  };

  useEffect(() => {
    fetchCampaigns();
    fetchCounts();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('notification_campaigns')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching campaigns:', error);
    } else {
      setCampaigns(data || []);
    }
    setLoading(false);
  };

  const fetchCounts = async () => {
    // Count customers with push tokens
    const { count: totalCustomers } = await supabase
      .from('user_profiles')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', true);
    setCustomerCount(totalCustomers || 0);

    // Count today's birthdays - query only users with DOB set
    const todayStr = new Date().toISOString().slice(5, 10); // "MM-DD" format
    console.log('🎂 Today string:', todayStr);
    
    const { data: dobUsers } = await supabase
      .from('user_profiles')
      .select('id, date_of_birth')
      .eq('is_active', true)
      .not('date_of_birth', 'is', null);

    console.log('🎂 Customers with DOB:', (dobUsers || []).length);
    const birthdayUsers = (dobUsers || []).filter(c => {
      const match = c.date_of_birth?.slice(5, 10) === todayStr;
      if (match) console.log('🎂 Birthday match:', c);
      return match;
    });
    console.log('🎂 Birthday users found:', birthdayUsers.length);
    setBirthdayCount(birthdayUsers.length);
  };

  const handleSendNotification = async () => {
    if (!composeData.title.trim() || !composeData.body.trim()) {
      alert('Please enter both a title and message');
      return;
    }

    const confirmed = window.confirm(
      `Send this notification to ${customerCount} customers?\n\nTitle: ${composeData.title}\nMessage: ${composeData.body}`
    );
    if (!confirmed) return;

    setSending(true);
    try {
      // Create campaign record
      const { data: campaign, error: createError } = await supabase
        .from('notification_campaigns')
        .insert({
          title: composeData.title,
          body: composeData.body,
          type: composeData.type,
          target: 'all',
          status: 'sending',
          created_by: user?.id
        })
        .select()
        .single();

      if (createError) throw createError;

      // Send via Edge Function
      const { data: result, error: sendError } = await supabase.functions.invoke('send-bulk-notification', {
        body: {
          campaign_id: campaign.id,
          title: composeData.title,
          body: composeData.body,
          type: composeData.type,
          target: 'all'
        }
      });

      if (sendError) throw sendError;

      alert(`✅ Notification sent!\n\nDelivered: ${result.sent_count}\nFailed: ${result.failed_count}`);
      setComposeData({ title: '', body: '', type: 'promo' });
      setShowCompose(false);
      fetchCampaigns();
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('❌ Failed to send: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const handleSendBirthday = async () => {
    if (birthdayCount === 0) {
      alert('No customers have a birthday today');
      return;
    }

    const confirmed = window.confirm(
      `Send birthday wishes to ${birthdayCount} customer(s) celebrating today?`
    );
    if (!confirmed) return;

    setSending(true);
    try {
      const { data: result, error } = await supabase.functions.invoke('send-bulk-notification', {
        body: {
          title: '🎂 Happy Birthday, {name}!',
          body: 'Wishing you a wonderful birthday! 🎉 Enjoy your special day with a treat from Foodline Hyper!',
          type: 'birthday',
          target: 'birthday_today'
        }
      });

      if (error) throw error;

      // Create campaign record after send
      await supabase.from('notification_campaigns').insert({
        title: '🎂 Happy Birthday!',
        body: 'Automated birthday wishes',
        type: 'birthday',
        target: 'birthday_today',
        status: 'sent',
        sent_count: result.sent_count,
        failed_count: result.failed_count,
        sent_at: new Date().toISOString(),
        created_by: user?.id
      });

      alert(`🎂 Birthday wishes sent to ${result.sent_count} customer(s)!`);
      fetchCampaigns();
    } catch (error) {
      console.error('Error sending birthday notifications:', error);
      alert('❌ Failed: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm('Delete this notification record?')) return;
    
    const { error } = await supabase
      .from('notification_campaigns')
      .delete()
      .eq('id', id);
    
    if (error) {
      alert('Failed to delete: ' + error.message);
    } else {
      setCampaigns(prev => prev.filter(c => c.id !== id));
    }
  };

  const fetchRecipients = async (campaignId, campaignType, sentAt) => {
    if (campaignRecipients[campaignId]) return;
    
    // Match notifications by type and same day
    const sentDate = new Date(sentAt || new Date());
    const dayStart = new Date(sentDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(sentDate);
    dayEnd.setHours(23, 59, 59, 999);
    
    const { data, error } = await supabase
      .from('notifications')
      .select('user_id, title')
      .eq('type', campaignType)
      .gte('created_at', dayStart.toISOString())
      .lte('created_at', dayEnd.toISOString())
      .limit(100);

    if (!error && data && data.length > 0) {
      const userIds = [...new Set(data.map(n => n.user_id))];
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, name, email')
        .in('id', userIds);

      const profileMap = {};
      (profiles || []).forEach(p => { profileMap[p.id] = p; });

      setCampaignRecipients(prev => ({
        ...prev,
        [campaignId]: userIds.map(uid => ({
          name: profileMap[uid]?.name || 'Unknown',
          email: profileMap[uid]?.email || '',
          title: data.find(n => n.user_id === uid)?.title || ''
        }))
      }));
    } else {
      setCampaignRecipients(prev => ({ ...prev, [campaignId]: [] }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-ZA', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'birthday': return <Gift size={16} className="text-pink-500" />;
      case 'promo': return <Megaphone size={16} className="text-blue-500" />;
      case 'announcement': return <Bell size={16} className="text-orange-500" />;
      default: return <Bell size={16} className="text-gray-500" />;
    }
  };

  const getTypeBadge = (type) => {
    const styles = {
      birthday: 'bg-pink-100 text-pink-700',
      promo: 'bg-blue-100 text-blue-700',
      announcement: 'bg-orange-100 text-orange-700'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${styles[type] || 'bg-gray-100 text-gray-700'}`}>
        {type === 'birthday' ? '🎂 Birthday' : type === 'promo' ? '📢 Promo' : '📣 Announcement'}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      sending: 'bg-yellow-100 text-yellow-700',
      sent: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700'
    };
    const icons = {
      draft: <Clock size={12} />,
      sending: <RefreshCw size={12} className="animate-spin" />,
      sent: <CheckCircle size={12} />,
      failed: <XCircle size={12} />
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {icons[status]} {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell size={28} className="text-primary" />
            Push Notifications
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Send promotions, announcements, and birthday wishes to customers
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <Users size={16} />
            Reachable Customers
          </div>
          <div className="text-2xl font-bold text-gray-900">{customerCount}</div>
          <div className="text-xs text-gray-400">With push tokens</div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-pink-500 text-sm mb-1">
            <Gift size={16} />
            Birthdays Today
          </div>
          <div className="text-2xl font-bold text-gray-900">{birthdayCount}</div>
          <div className="text-xs text-gray-400">Customers celebrating</div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 col-span-2 sm:col-span-1">
          <div className="flex items-center gap-2 text-blue-500 text-sm mb-1">
            <Megaphone size={16} />
            Campaigns Sent
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {campaigns.filter(c => c.status === 'sent').length}
          </div>
          <div className="text-xs text-gray-400">Total notifications</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <button
          onClick={() => setShowCompose(!showCompose)}
          className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-3 px-4 rounded-xl hover:bg-blue-700 transition-colors"
        >
          <Megaphone size={20} />
          {showCompose ? 'Cancel' : 'New Notification'}
        </button>

        <button
          onClick={handleSendBirthday}
          disabled={sending || birthdayCount === 0}
          className="flex-1 flex items-center justify-center gap-2 bg-pink-500 text-white font-semibold py-3 px-4 rounded-xl hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Gift size={20} />
          Send Birthday Wishes ({birthdayCount})
        </button>
      </div>

      {/* Compose Form */}
      {showCompose && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Send size={20} />
            Compose Notification
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setComposeData({ ...composeData, type: 'promo' })}
                  className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium text-sm transition-all ${
                    composeData.type === 'promo' 
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  📢 Promo / Deal
                </button>
                <button
                  onClick={() => setComposeData({ ...composeData, type: 'announcement' })}
                  className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium text-sm transition-all ${
                    composeData.type === 'announcement' 
                      ? 'border-orange-500 bg-orange-50 text-orange-700' 
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  📣 Announcement
                </button>
              </div>
            </div>

            {/* Emoji Picker */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase">Quick Emojis</p>
                <p className="text-xs text-gray-400">
                  {activeEmojiField ? `Adding to ${activeEmojiField}` : 'Click a text field first'}
                </p>
              </div>
              {emojiCategories.map((cat, i) => (
                <div key={i} className="mb-2 last:mb-0">
                  <p className="text-xs text-gray-400 mb-1">{cat.label}</p>
                  <div className="flex flex-wrap gap-1">
                    {cat.emojis.map((emoji, j) => (
                      <button
                        key={j}
                        type="button"
                        onClick={() => insertEmoji(emoji)}
                        disabled={!activeEmojiField}
                        className="w-8 h-8 flex items-center justify-center rounded hover:bg-white hover:shadow-sm transition-all text-lg disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={composeData.title}
                onFocus={() => setActiveEmojiField('title')}
                onChange={(e) => setComposeData({ ...composeData, title: e.target.value })}
                placeholder={composeData.type === 'promo' ? 'e.g., 🔥 Weekend Super Sale!' : 'e.g., Store Hours Update'}
                maxLength={100}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-400 mt-1">{composeData.title.length}/100 characters</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={composeData.body}
                onFocus={() => setActiveEmojiField('body')}
                onChange={(e) => setComposeData({ ...composeData, body: e.target.value })}
                placeholder={composeData.type === 'promo' 
                  ? 'e.g., Get up to 50% off on selected items this weekend only! Shop now on Foodline Hyper.' 
                  : 'e.g., Please note our store will be closed on Monday 1 May for Workers Day.'}
                rows={3}
                maxLength={300}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">{composeData.body.length}/300 characters</p>
            </div>

            {/* Preview */}
            {(composeData.title || composeData.body) && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-semibold text-gray-500 mb-2 uppercase">Preview</p>
                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                  <div className="flex items-start gap-2">
                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">FH</span>
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-gray-900">{composeData.title || 'Notification Title'}</p>
                      <p className="text-sm text-gray-600 mt-0.5">{composeData.body || 'Notification message...'}</p>
                      <p className="text-xs text-gray-400 mt-1">Foodline Hyper • now</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Test Email</label>
                  <input
                    type="email"
                    value={composeData.testEmail || ''}
                    onChange={(e) => setComposeData({ ...composeData, testEmail: e.target.value })}
                    placeholder="e.g., ahmeds.rsa@gmail.com"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400 text-sm"
                  />
                </div>
                <button
                  onClick={async () => {
                    if (!composeData.title.trim() || !composeData.body.trim()) {
                      alert('Please enter both a title and message');
                      return;
                    }
                    if (!composeData.testEmail?.trim()) {
                      alert('Please enter a test email address');
                      return;
                    }
                    setSending(true);
                    try {
                      const { data: result, error } = await supabase.functions.invoke('send-bulk-notification', {
                        body: {
                          title: composeData.title,
                          body: composeData.body,
                          type: composeData.type,
                          target: 'test_email',
                          test_email: composeData.testEmail.trim().toLowerCase()
                        }
                      });
                      if (error) throw error;
                      if (result.total_users === 0) {
                        alert('❌ No account found with that email address');
                      } else {
                        alert(`✅ Test sent to ${composeData.testEmail}!\nPush: ${result.sent_count > 0 ? 'Delivered' : 'No push token'}\nIn-app: Created`);
                      }
                    } catch (error) {
                      alert('❌ Failed: ' + error.message);
                    } finally {
                      setSending(false);
                    }
                  }}
                  disabled={sending || !composeData.title.trim() || !composeData.body.trim() || !composeData.testEmail?.trim()}
                  className="self-end flex items-center gap-2 bg-gray-600 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  <Bell size={18} />
                  Send Test
                </button>
              </div>

              <button
                onClick={handleSendNotification}
                disabled={sending || !composeData.title.trim() || !composeData.body.trim()}
                className="w-full flex items-center justify-center gap-2 bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {sending ? (
                  <>
                    <RefreshCw size={20} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Send to All Customers ({customerCount})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Clock size={20} />
            Notification History
          </h2>
          <button
            onClick={fetchCampaigns}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw size={18} className="text-gray-500" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
            Loading...
          </div>
        ) : campaigns.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Bell size={32} className="mx-auto mb-2 opacity-50" />
            <p>No notifications sent yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {campaigns.map(campaign => (
              <div key={campaign.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedCampaign(expandedCampaign === campaign.id ? null : campaign.id)}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {getTypeIcon(campaign.type)}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">
                        {campaign.type === 'birthday' 
                          ? `🎂 Birthday Wishes — ${formatDate(campaign.sent_at || campaign.created_at).split(',')[0]}`
                          : campaign.title
                        }
                      </p>
                      <p className="text-xs text-gray-500">
                        {campaign.type === 'birthday' ? (
                          campaign.sent_count > 0 
                            ? `Delivered to ${campaign.sent_count} customer${campaign.sent_count > 1 ? 's' : ''}`
                            : 'No birthdays on this day'
                        ) : (
                          campaign.sent_count > 0
                            ? `Delivered to ${campaign.sent_count} customer${campaign.sent_count > 1 ? 's' : ''} • ${formatDate(campaign.sent_at || campaign.created_at)}`
                            : formatDate(campaign.sent_at || campaign.created_at)
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getTypeBadge(campaign.type)}
                    {getStatusBadge(campaign.status)}
                    {expandedCampaign === campaign.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>

                {expandedCampaign === campaign.id && (
                  <div className="mt-3 pl-7 space-y-2">
                    <p className="text-sm text-gray-600">{campaign.body}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                      {campaign.sent_count > 0 && (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle size={12} /> {campaign.sent_count} push delivered
                        </span>
                      )}
                      {campaign.failed_count > 0 && (
                        <span className="flex items-center gap-1 text-orange-500">
                          <XCircle size={12} /> {campaign.failed_count} push failed (in-app still delivered)
                        </span>
                      )}
                      <span>Target: {campaign.target === 'all' ? 'All customers' : 'Birthday customers'}</span>
                    </div>
                    {campaign.failed_count > 0 && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 mt-2 text-xs text-orange-700">
                        <p className="font-semibold mb-1">Why do push notifications fail?</p>
                        <p>Push notifications require the customer to have granted permission on their device. Common reasons for failure:</p>
                        <ul className="list-disc ml-4 mt-1 space-y-0.5">
                          <li>iPhone users who haven't added the app to their Home Screen</li>
                          <li>Customers who declined or haven't enabled push notifications</li>
                          <li>Expired or invalid device tokens</li>
                        </ul>
                        <p className="mt-1 font-medium">These customers will still see the notification in-app when they next open Foodline Hyper.</p>
                      </div>
                    )}
                    
                    {/* Recipients */}
                    <div className="mt-2">
                      {!campaignRecipients[campaign.id] ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); fetchRecipients(campaign.id, campaign.type, campaign.sent_at || campaign.created_at); }}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Users size={12} /> View recipients
                        </button>
                      ) : campaignRecipients[campaign.id].length === 0 ? (
                        <p className="text-xs text-gray-400">No recipient data available</p>
                      ) : (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">
                            Recipients ({campaignRecipients[campaign.id].length}):
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {campaignRecipients[campaign.id].map((r, i) => (
                              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                                {r.name}
                                {r.email && <span className="text-gray-400 ml-1">({r.email})</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteCampaign(campaign.id); }}
                      className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 mt-2"
                    >
                      <Trash2 size={12} /> Delete record
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotifications;
